import React, { useEffect, useState, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Minus, RefreshCw, Loader2, Target, BarChart3,
  Archive, RotateCcw, AlertCircle, Plus, X, Check, GitFork,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  decline: {
    label: 'Decline prompts',
    description: '"Your X dropped — model an intervention →"',
    Icon: TrendingDown,
    accent: 'text-rose-600',
    barCls: 'bg-rose-500',
    chipCls: 'bg-rose-100 text-rose-700',
  },
  gain: {
    label: 'Gain prompts',
    description: '"Your X climbed — bottle the recipe →"',
    Icon: TrendingUp,
    accent: 'text-emerald-600',
    barCls: 'bg-emerald-500',
    chipCls: 'bg-emerald-100 text-emerald-700',
  },
  fallback: {
    label: 'Fallback prompts',
    description: '"Inputs draw from your live ROE feed…"',
    Icon: Minus,
    accent: 'text-slate-600',
    barCls: 'bg-slate-500',
    chipCls: 'bg-slate-200 text-slate-700',
  },
};

const LOOKBACK_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

/**
 * NarrativeBanditLeaderboard — admin view of how each phrasing
 * variant in the BCB hero-narrative bandit is performing. Reads
 * `/business-case-builder/analytics/hero-narrative-stats` and
 * renders:
 *   • per-`kind` rollup card (total viewed / clicked / CTR%)
 *   • a sub-list of variants ranked by CTR with horizontal bars
 *   • a "🥇 leader" badge on the top performer once it has
 *     enough samples to be statistically meaningful (≥30 views)
 *
 * Lets PMs make actionable copy decisions ("retire variant X,
 * lean into variant Y") instead of guessing.
 */
export function NarrativeBanditLeaderboard() {
  const [lookbackDays, setLookbackDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(
        `${API_URL}/api/business-case-builder/analytics/hero-narrative-stats?lookback_days=${lookbackDays}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Could not load stats');
    } finally {
      setLoading(false);
    }
  }, [lookbackDays]);

  // Toggle retire/restore on a variant. Optimistic? Nah — we
  // refetch so the leaderboard always reflects server truth
  // (incl. recomputed leader_per_kind and auto_retire flags).
  const [mutating, setMutating] = useState(null);
  const toggleRetire = async (variantId, isRetired) => {
    setMutating(variantId);
    try {
      const token = secureStorage.getItem('session_token');
      const action = isRetired ? 'restore' : 'retire';
      const r = await authedFetch(
        `${API_URL}/api/business-case-builder/analytics/variants/${variantId}/${action}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `${action} failed`);
      }
      toast.success(isRetired ? 'Variant restored' : 'Variant retired');
      await fetchStats();
    } catch (e) {
      toast.error(e.message || 'Could not update variant');
    } finally {
      setMutating(null);
    }
  };

  // Composer state for runtime-authored variants. Keyed by kind so
  // each card has its own open/close/draft state — opening the
  // "decline" composer doesn't nuke an in-progress "gain" draft.
  const [composer, setComposer] = useState({});
  const setKindComposer = (kind, patch) =>
    setComposer(prev => ({
      ...prev,
      [kind]: { ...(prev[kind] || {}), ...patch },
    }));
  const openComposer = (kind) =>
    setKindComposer(kind, { open: true, suffix: '', slug: '', saving: false });
  const forkVariantToComposer = (kind, sourceSuffix) => {
    // Pre-fill the composer with the source variant's suffix so the
    // PM can riff on a known performer instead of authoring blind.
    setKindComposer(kind, {
      open: true,
      suffix: sourceSuffix || '',
      slug: '',
      saving: false,
    });
    // Best-effort scroll: the composer is in the same card header
    // so DOM-find by data-testid and ensure it's in view.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="bandit-composer-suffix-${kind}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 50);
  };
  const closeComposer = (kind) => setKindComposer(kind, { open: false });
  const createVariant = async (kind) => {
    const draft = composer[kind] || {};
    const suffix = (draft.suffix || '').trim();
    if (suffix.length < 4) {
      toast.error('Variant text must be at least 4 characters.');
      return;
    }
    if (suffix.length > 200) {
      toast.error('Variant text must be 200 characters or fewer.');
      return;
    }
    setKindComposer(kind, { saving: true });
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(
        `${API_URL}/api/business-case-builder/analytics/variants`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            kind,
            suffix,
            slug: (draft.slug || '').trim() || null,
          }),
        }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not create variant');
      }
      toast.success('Custom variant added to the bandit pool.');
      setKindComposer(kind, { open: false, suffix: '', slug: '', saving: false });
      await fetchStats();
    } catch (e) {
      toast.error(e.message || 'Could not create variant');
      setKindComposer(kind, { saving: false });
    }
  };

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  const renderKindCard = (kind) => {
    const meta = KIND_META[kind];
    if (!meta) return null;
    const rollup = data?.by_kind?.[kind] || { viewed: 0, clicked: 0, conversion_pct: 0 };
    const variants = data?.by_variant?.[kind] || {};
    const variantRows = Object.entries(variants)
      .map(([vid, v]) => ({ vid, ...v }))
      .sort((a, b) => (b.conversion_pct || 0) - (a.conversion_pct || 0));

    // Server-computed statistical leader: top variant only if a
    // two-proportion z-test vs the runner-up returns p < 0.05.
    // Falls back to null when the data is too noisy (small n,
    // ties, etc.) — better to show no badge than crown noise.
    const leaderInfo = data?.leader_per_kind?.[kind];
    const leaderVid = leaderInfo?.is_significant ? leaderInfo.variant_id : null;
    const leaderP = leaderInfo?.p_value;
    const autoRetireFlags = new Set(data?.auto_retire_candidates?.[kind] || []);

    const Icon = meta.Icon;
    const kindComposer = composer[kind] || {};

    return (
      <Card key={kind} data-testid={`bandit-leaderboard-card-${kind}`} className="flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className={`text-base flex items-center gap-2 ${meta.accent}`}>
              <Icon size={16} />
              {meta.label}
            </CardTitle>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${meta.chipCls}`}>
              {(rollup.conversion_pct ?? 0).toFixed(1)}% CTR
            </span>
          </div>
          <CardDescription className="text-xs">{meta.description}</CardDescription>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-600 mt-2">
            <div className="flex items-center gap-3">
              <span><strong className="text-slate-900">{rollup.viewed || 0}</strong> views</span>
              <span><strong className="text-slate-900">{rollup.clicked || 0}</strong> clicks</span>
            </div>
            {!kindComposer.open && (
              <button
                onClick={() => openComposer(kind)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900 hover:bg-violet-50 px-1.5 py-0.5 rounded"
                data-testid={`bandit-add-variant-open-${kind}`}
              >
                <Plus size={11} /> Add variant
              </button>
            )}
          </div>
          {kindComposer.open && (
            <div
              className="mt-2 p-2 rounded border border-violet-200 bg-violet-50/60 space-y-1.5"
              data-testid={`bandit-composer-${kind}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-violet-800">
                  New {meta.label.toLowerCase()} copy
                </span>
                <button
                  onClick={() => closeComposer(kind)}
                  className="text-slate-500 hover:text-slate-800"
                  aria-label="Close composer"
                  data-testid={`bandit-composer-close-${kind}`}
                >
                  <X size={12} />
                </button>
              </div>
              <textarea
                value={kindComposer.suffix || ''}
                onChange={(e) => setKindComposer(kind, { suffix: e.target.value })}
                placeholder="e.g. show me the next play →"
                rows={2}
                maxLength={200}
                className="w-full text-xs p-1.5 rounded border border-violet-200 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
                data-testid={`bandit-composer-suffix-${kind}`}
              />
              <input
                type="text"
                value={kindComposer.slug || ''}
                onChange={(e) => setKindComposer(kind, { slug: e.target.value })}
                placeholder="Optional slug (internal id)"
                maxLength={32}
                className="w-full text-[11px] p-1 rounded border border-violet-200 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400"
                data-testid={`bandit-composer-slug-${kind}`}
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500">
                  {(kindComposer.suffix || '').length}/200 · rendered after your "X dropped —" prefix
                </span>
                <button
                  onClick={() => createVariant(kind)}
                  disabled={kindComposer.saving}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold bg-violet-600 hover:bg-violet-700 text-white px-2 py-0.5 rounded disabled:opacity-50"
                  data-testid={`bandit-composer-save-${kind}`}
                >
                  {kindComposer.saving ? (
                    <Loader2 className="animate-spin" size={11} />
                  ) : (
                    <Check size={11} />
                  )}
                  Add
                </button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="flex-1 space-y-2">
          {variantRows.length === 0 && (
            <div className="text-xs italic text-slate-400">
              No variant data yet. Variants will appear once they're shown to users.
            </div>
          )}
          {variantRows.map((v) => {
            const isLeader = leaderVid && v.vid === leaderVid;
            const isRetired = !!v.is_retired;
            const isAutoRetireCandidate = autoRetireFlags.has(v.vid);
            const ctr = v.conversion_pct || 0;
            return (
              <div
                key={v.vid}
                className={`text-xs rounded p-2 transition-colors ${
                  isRetired ? 'bg-slate-50 opacity-60' : ''
                }`}
                data-testid={`bandit-leaderboard-variant-${v.vid}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono text-[11px] text-slate-700 truncate flex items-center gap-1 flex-1 min-w-0">
                    {isLeader && (
                      <span title={`Statistically better than #2 (p=${leaderP ?? '—'})`}>🥇</span>
                    )}
                    {isRetired && (
                      <span className="text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-slate-200 text-slate-600 shrink-0">
                        Retired
                      </span>
                    )}
                    {isAutoRetireCandidate && !isRetired && (
                      <span
                        className="text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0 flex items-center gap-0.5"
                        title="Statistically losing vs leader with ≥200 samples — consider retiring"
                      >
                        <AlertCircle size={9} /> Suggest retire
                      </span>
                    )}
                    {v.is_custom && (
                      <span
                        className="text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-violet-100 text-violet-700 shrink-0"
                        title="PM-authored variant (not from the built-in catalog)"
                      >
                        Custom
                      </span>
                    )}
                    <span className="truncate">{v.vid}</span>
                  </span>
                  <span className="font-bold text-slate-900 tabular-nums">
                    {ctr.toFixed(1)}%
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 italic mb-1 truncate">
                  {v.suffix}
                </div>
                <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                  <div
                    className={`h-full ${isRetired ? 'bg-slate-400' : meta.barCls} transition-all`}
                    style={{ width: `${Math.min(100, ctr)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <div className="flex items-center gap-3 text-[10px] text-slate-500">
                    <span>{v.viewed || 0} views</span>
                    <span>{v.clicked || 0} clicks</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => forkVariantToComposer(kind, v.suffix)}
                      className="text-[10px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 transition-colors"
                      title="Pre-fill the composer with this copy so you can tweak and save as a new variant"
                      data-testid={`bandit-fork-${v.vid}`}
                    >
                      <GitFork size={10} />
                      Fork
                    </button>
                    <button
                      onClick={() => toggleRetire(v.vid, isRetired)}
                      disabled={mutating === v.vid}
                      className={`text-[10px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors ${
                        isRetired
                          ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                          : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`bandit-toggle-retire-${v.vid}`}
                    >
                      {mutating === v.vid ? (
                        <Loader2 className="animate-spin" size={10} />
                      ) : isRetired ? (
                        <RotateCcw size={10} />
                      ) : (
                        <Archive size={10} />
                      )}
                      {isRetired ? 'Restore' : 'Retire'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  return (
    <Card data-testid="narrative-bandit-leaderboard">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 size={18} className="text-violet-600" />
              Hero-narrative bandit — variant performance
            </CardTitle>
            <CardDescription>
              Live click-through rates for each phrasing variant on the BCB hero panel.
              Thompson sampling auto-rotates copy; this view shows which variant is winning so you can
              retire losers and double down on the leader.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {LOOKBACK_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setLookbackDays(opt.value)}
                  className={`px-2.5 py-1 text-xs font-medium ${
                    lookbackDays === opt.value
                      ? 'bg-violet-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`bandit-leaderboard-lookback-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchStats}
              disabled={loading}
              data-testid="bandit-leaderboard-refresh"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-800">
            {error}
          </div>
        )}
        {loading && !data && (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm gap-2">
            <Loader2 className="animate-spin" size={16} /> Loading bandit stats…
          </div>
        )}
        {data && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {['decline', 'gain', 'fallback'].map(renderKindCard)}
            </div>
            <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-2">
              <Target size={12} />
              Window: last {lookbackDays} days · since {new Date(data.since).toLocaleDateString()}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default NarrativeBanditLeaderboard;
